"use client";

import React, { useState, useEffect } from "react";
import { NativeBarChart } from "@/components/ui/NativeBarChart";
import { Info, Lightbulb } from "lucide-react";

const formatCurrency = (value: number) => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

export function IPOFrictionModeler({
  initialTrustSize,
}: {
  initialTrustSize?: number;
}) {
  const [targetEV, setTargetEV] = useState<number>(1000000000);
  const [targetRaise, setTargetRaise] = useState<number>(200000000);
  const [spacRedemptions, setSpacRedemptions] = useState<number>(0);
  const [spacTrustSize, setSpacTrustSize] = useState<number>(
    initialTrustSize || 345000000,
  );

  // Traditional IPO Math
  const ipoGrossSpreadPct = 0.07;
  const ipoGrossSpread = targetRaise * ipoGrossSpreadPct;
  const ipoRoadshowFees = 3000000;
  const ipoDAndO = 4000000;
  const ipoTotalCashCosts = ipoGrossSpread + ipoRoadshowFees + ipoDAndO;
  const ipoNetCash = targetRaise - ipoTotalCashCosts;
  const ipoDiscountPct = 0.15;
  const ipoDiscountValue = targetEV * ipoDiscountPct; // Money left on the table

  // Meshflow SPAC Math
  const remainingTrust = spacTrustSize * (1 - spacRedemptions / 100);
  const pipeRequired = Math.max(0, targetRaise - remainingTrust);
  const spacGrossProceeds = remainingTrust + pipeRequired;

  const spacAdvisoryFees = 5000000 + pipeRequired * 0.03; // 3% on PIPE
  const spacLegalFees = 3000000;
  const spacDeferredFees = 8000000;
  const spacDAndO = 3000000;
  const spacTotalCashCosts =
    spacAdvisoryFees + spacLegalFees + spacDeferredFees + spacDAndO;
  const spacNetCash = spacGrossProceeds - spacTotalCashCosts;
  const spacDilutionCost = targetEV * 0.05; // Simplifying sponsor promote/warrant impact to 5% of EV for comparison

  const cashDifference = Math.max(0, spacNetCash) - ipoNetCash;
  const isSpacBetter = cashDifference > 0;

  const insightText = isSpacBetter
    ? `In this scenario, a SPAC transaction delivers ${formatCurrency(cashDifference)} more net cash to the balance sheet than a traditional IPO, largely due to reduced total friction and optimized underwriting fees.`
    : cashDifference < 0
      ? `In this scenario, a traditional IPO delivers ${formatCurrency(Math.abs(cashDifference))} more net cash to the balance sheet than the SPAC route, largely because of the high redemptions leaving less capital in trust.`
      : `In this scenario, both the SPAC and traditional IPO routes deliver the exact same net cash to the balance sheet.`;

  const chartData = [
    {
      name: "Traditional IPO",
      datasets: [
        {
          label: "Net Cash to Balance Sheet",
          value: ipoNetCash,
          color: "#10b981",
        },
        { label: "Cash Friction", value: ipoTotalCashCosts, color: "#ef4444" },
        {
          label: "Value Left on Table",
          value: ipoDiscountValue,
          color: "#f97316",
        },
      ],
    },
    {
      name: "Meshflow SPAC",
      datasets: [
        {
          label: "Net Cash to Balance Sheet",
          value: Math.max(0, spacNetCash),
          color: "#10b981",
        },
        { label: "Cash Friction", value: spacTotalCashCosts, color: "#ef4444" },
        {
          label: "Value Left on Table",
          value: spacDilutionCost,
          color: "#f97316",
        },
      ],
    },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6 bg-card rounded-2xl border border-border shadow-sm">
        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Target Enterprise Value (EV)
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(targetEV)}
            </span>
          </label>
          <input
            type="range"
            min={500000000}
            max={5000000000}
            step={100000000}
            value={targetEV}
            onChange={(e) => setTargetEV(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Target Capital Raise
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(targetRaise)}
            </span>
          </label>
          <input
            type="range"
            min={50000000}
            max={1000000000}
            step={25000000}
            value={targetRaise}
            onChange={(e) => setTargetRaise(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            SPAC Trust Redemptions
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {spacRedemptions}%
            </span>
          </label>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={spacRedemptions}
            onChange={(e) => setSpacRedemptions(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        {/* Cost Breakdown Cards */}
        <div className="lg:col-span-1 space-y-4">
          <div className="p-5 bg-card rounded-xl border border-border shadow-sm">
            <h3 className="text-lg font-bold border-b border-border pb-2 mb-4">
              Traditional IPO Profile
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gross Spread (7%)</span>
                <span className="font-mono">
                  {formatCurrency(ipoGrossSpread)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Roadshow & Listing
                </span>
                <span className="font-mono">
                  {formatCurrency(ipoRoadshowFees)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  D&O Insurance (Yr 1)
                </span>
                <span className="font-mono">{formatCurrency(ipoDAndO)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border font-semibold">
                <span>Total Cash Friction</span>
                <span className="font-mono text-destructive">
                  {formatCurrency(ipoTotalCashCosts)}
                </span>
              </div>
              <div className="flex justify-between pt-2 mt-2 border-t border-border">
                <span className="text-muted-foreground flex items-center gap-1">
                  IPO Discount (15%) <Info className="w-3 h-3" />
                </span>
                <span className="font-mono text-orange-500">
                  {formatCurrency(ipoDiscountValue)}
                </span>
              </div>
            </div>
          </div>

          <div className="p-5 bg-primary/10 rounded-xl border border-primary/20 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/20 rounded-full blur-2xl -mr-10 -mt-10"></div>
            <h3 className="text-lg font-bold border-b border-primary/20 pb-2 mb-4 text-primary">
              Meshflow Profile
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Advisory & Legal</span>
                <span className="font-mono">
                  {formatCurrency(spacAdvisoryFees + spacLegalFees)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Deferred Underwriting
                </span>
                <span className="font-mono">
                  {formatCurrency(spacDeferredFees)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  D&O Insurance (Yr 1)
                </span>
                <span className="font-mono">{formatCurrency(spacDAndO)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-primary/20 font-semibold">
                <span className="text-foreground">Total Cash Friction</span>
                <span className="font-mono text-primary">
                  {formatCurrency(spacTotalCashCosts)}
                </span>
              </div>
              <div className="flex justify-between pt-2 mt-2 border-t border-primary/20">
                <span className="text-muted-foreground flex items-center gap-1">
                  Structural Dilution <Info className="w-3 h-3" />
                </span>
                <span className="font-mono text-orange-500">
                  {formatCurrency(spacDilutionCost)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="lg:col-span-2 p-6 bg-card rounded-2xl border border-border shadow-sm flex flex-col justify-center min-h-[400px]">
          <div className="w-full h-full pb-4">
            <NativeBarChart data={chartData} formatCurrency={formatCurrency} />
          </div>
        </div>
      </div>

      {/* Dynamic Data Insight */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 items-start shadow-sm mt-8">
        <div className="p-3 bg-primary/10 rounded-full text-primary mt-1 flex-shrink-0">
          <Lightbulb size={24} />
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-1 text-lg">
            Key Takeaway
          </h4>
          <p className="text-muted-foreground text-lg text-balance leading-relaxed">
            {insightText}
          </p>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
